/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Square shell — Perfil público de la tienda online.
 * Scroll unificado: PaperSheet blocks (sin rail+tabs).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  PanResponder,
  Platform,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  type TextInput as TextInputType,
} from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { showInfo } from '../../../shared/ui/appToast';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { nationalPartFromStored } from '../../domain/storeContactPhone';
import {
  isDarkStoreTheme,
  isValidSlug,
  normalizeSlug,
  type BusinessHourDay,
  type StoreProfileDraft,
  type StoreTheme,
  type TimeShift,
} from '../../domain/storeProfileTypes';
import { StoreDoorStatusCard } from './StoreDoorStatusCard';
import { StoreHoursEditor } from './StoreHoursEditor';
import { StorefrontCopyLinkCard } from './StorefrontCopyLinkCard';
import { QrGeneratorSection } from './QrGeneratorSection';
import { NotifyCustomerReadyPickupSwitchRow } from '../../../shared/storefront/NotifyCustomerReadyPickupSwitchRow';

type Props = {
  draft: StoreProfileDraft;
  saving: boolean;
  dirty: boolean;
  logoUploading: boolean;
  coverUploading: boolean;
  backgroundUploading: boolean;
  canEdit: boolean;
  canEditProfile?: boolean;
  canEditHours?: boolean;
  canPublish?: boolean;
  businessTypeCode: string | null;
  /** Si la org tiene cola de cocina (aviso automático operativo al marcar listo). */
  kitchenTicketsEnabled?: boolean;
  onDutyAllowed?: boolean;
  branchName: string;
  portalBaseUrl: string;
  onChange: (patch: Partial<StoreProfileDraft>) => void;
  onBusinessHourChange: (dayOfWeek: number, patch: Partial<BusinessHourDay>) => void;
  onAddShift: (dayOfWeek: number) => void;
  onRemoveShift: (dayOfWeek: number, index: number) => void;
  onShiftChange: (dayOfWeek: number, index: number, field: keyof TimeShift, value: string) => void;
  onRequestForceClose: (durationMinutes?: number) => void;
  onRequestForceOpen: () => void;
  onRequestReturnToSchedule: () => void;
  onRequestDeactivatePublic: () => void;
  tcAcceptedAt: string | null;
  onTcRequired: () => void;
  onUploadLogo: () => void;
  onRemoveLogo: () => void;
  onUploadCover: () => void;
  onRemoveCover: () => void;
  onUploadBackground: () => void;
  onRemoveBackground: () => void;
  onSave: () => void;
  /** Debounced slug availability check (branch-aware). */
  onCheckSlug?: (slug: string) => Promise<boolean>;
};

function PhonePrefixField({
  label,
  value,
  onChangeText,
  editable,
  inputRef,
  placeholder,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  editable: boolean;
  inputRef?: React.RefObject<TextInputType | null>;
  placeholder: string;
}) {
  return (
    <View>
      <Text style={styles.captionStrong}>{label}</Text>
      <View style={local.slugPrefix}>
        <Text style={local.prefixLabel}>+54</Text>
        <TextInput
          ref={inputRef}
          style={[styles.input, local.prefixInput]}
          value={nationalPartFromStored(value)}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={ticketColors.ink3}
          keyboardType="phone-pad"
          editable={editable}
        />
      </View>
    </View>
  );
}

export function PublicStoreProfileSection({
  draft,
  saving,
  dirty: _dirty,
  logoUploading,
  coverUploading,
  backgroundUploading,
  canEdit,
  canEditProfile: canEditProfileProp,
  canEditHours: canEditHoursProp,
  canPublish: canPublishProp,
  businessTypeCode: _businessTypeCode,
  kitchenTicketsEnabled = false,
  onDutyAllowed = false,
  branchName,
  portalBaseUrl,
  onChange,
  onBusinessHourChange,
  onAddShift,
  onRemoveShift,
  onShiftChange,
  onRequestForceClose,
  onRequestForceOpen,
  onRequestReturnToSchedule,
  onRequestDeactivatePublic,
  tcAcceptedAt,
  onTcRequired,
  onUploadLogo,
  onRemoveLogo,
  onUploadCover,
  onRemoveCover,
  onUploadBackground,
  onRemoveBackground,
  onSave: _onSave,
  onCheckSlug,
}: Props) {
  const canEditProfile = canEditProfileProp ?? canEdit;
  const canEditHours = canEditHoursProp ?? canEdit;
  const canPublish = canPublishProp ?? canEdit;
  const phoneRef = useRef<TextInputType>(null);
  const whatsappRef = useRef<TextInputType>(null);

  // —— Slug availability (debounced) ——
  const [slugResult, setSlugResult] = useState<{
    slug: string;
    status: 'ok' | 'taken';
  } | null>(null);
  useEffect(() => {
    const slug = normalizeSlug(draft.slug);
    if (!onCheckSlug || !canEditProfile || !isValidSlug(slug)) return;
    const timer = setTimeout(() => {
      void onCheckSlug(slug)
        .then((ok) => setSlugResult({ slug, status: ok ? 'ok' : 'taken' }))
        .catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [draft.slug, onCheckSlug, canEditProfile]);

  const normalizedDraftSlug = normalizeSlug(draft.slug);
  const slugCheck: 'idle' | 'checking' | 'ok' | 'taken' | 'invalid' = !onCheckSlug
    ? 'idle'
    : !normalizedDraftSlug
      ? 'idle'
      : !isValidSlug(normalizedDraftSlug)
        ? 'invalid'
        : slugResult?.slug === normalizedDraftSlug
          ? slugResult.status
          : 'checking';

  const tryEnableWhatsappContact = (on: boolean) => {
    if (!on) {
      onChange({ orderContactWhatsapp: false });
      return;
    }
    if (!draft.whatsapp.trim()) {
      showInfo('WhatsApp', 'Completá el número de WhatsApp para usarlo en el pedido.');
      whatsappRef.current?.focus();
      return;
    }
    onChange({ orderContactWhatsapp: true });
  };

  const tryEnablePhoneContact = (on: boolean) => {
    if (!on) {
      onChange({ orderContactPhone: false });
      return;
    }
    if (!draft.phone.trim()) {
      showInfo('Teléfono', 'Completá el teléfono para permitir llamadas en el pedido.');
      phoneRef.current?.focus();
      return;
    }
    onChange({ orderContactPhone: true });
  };

  const hasWhatsapp = draft.whatsapp.trim().length > 0;
  const hasPhone = draft.phone.trim().length > 0;

  const requestActivatePublic = (isPublic: boolean) => {
    if (isPublic && !tcAcceptedAt) {
      onTcRequired();
      return;
    }
    if (!isPublic && draft.isPublic) {
      onRequestDeactivatePublic();
      return;
    }
    onChange({ isPublic });
  };

  const actionBtn = (
    label: string,
    onPress: () => void,
    opts?: { disabled?: boolean; ghost?: boolean; primary?: boolean; onCover?: boolean }
  ) => (
    <TouchableOpacity
      style={[
        local.actionBtn,
        opts?.ghost && local.actionBtnGhost,
        opts?.primary && local.actionBtnPrimary,
        opts?.onCover && local.actionBtnOnCover,
        opts?.disabled && styles.disabled,
      ]}
      onPress={onPress}
      disabled={opts?.disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Text
        style={[
          local.actionBtnText,
          opts?.ghost && local.actionBtnTextGhost,
          opts?.primary && local.actionBtnTextPrimary,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );

  // —— Door status ——

  const door = (
    <StoreDoorStatusCard
      draft={draft}
      canEdit={canEditHours}
      saving={saving}
      onRequestForceClose={onRequestForceClose}
      onRequestForceOpen={onRequestForceOpen}
      onRequestReturnToSchedule={onRequestReturnToSchedule}
    />
  );

  // —— Toggle tienda online ——

  const onlineToggle = (
    <PaperSheet padded>
      <View style={local.rowBetween}>
        <View style={{ flex: 1, paddingRight: spacing.md }}>
          <Text style={styles.captionStrong}>
            {draft.isPublic ? 'Tienda online activa' : 'Activar tienda online'}
          </Text>
          {draft.isPublic && draft.tcAcceptedAt ? (
            <Text style={styles.caption}>
              T&C aceptados el {new Date(draft.tcAcceptedAt).toLocaleDateString('es-AR')}
            </Text>
          ) : null}
        </View>
        <Switch
          value={draft.isPublic}
          onValueChange={requestActivatePublic}
          disabled={!canPublish || saving}
          trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
          thumbColor={ticketColors.ink}
        />
      </View>
    </PaperSheet>
  );

  // —— De guardia ——

  const dutySwitch = onDutyAllowed && draft.isPublic ? (
    <PaperSheet padded>
      <View style={local.rowBetween}>
        <View style={{ flex: 1, paddingRight: spacing.md }}>
          <Text style={styles.captionStrong}>De guardia</Text>
          <Text style={styles.caption}>Se ve en tu tienda.</Text>
        </View>
        <Switch
          value={draft.isOnDuty}
          onValueChange={(isOnDuty) => onChange({ isOnDuty })}
          disabled={!canEditProfile || saving}
          trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
          thumbColor={ticketColors.ink}
        />
      </View>
    </PaperSheet>
  ) : null;

  // —— Copy link ——

  const copyLink = draft.isPublic && draft.slug ? (
    <StorefrontCopyLinkCard slug={draft.slug} portalBaseUrl={portalBaseUrl} />
  ) : null;

  // —— Vitrina / Portada y logo ——

  const THEME_PALETTES: ReadonlyArray<{
    key: StoreTheme;
    label: string;
    bg: string;
    card: string;
    accent: string;
    dark: boolean;
  }> = [
    { key: 'light', label: 'Vuelto', bg: '#F4F9F7', card: '#FFFFFF', accent: '#0F766E', dark: false },
    { key: 'cream', label: 'Crema', bg: '#FAF5EE', card: '#FFFFFF', accent: '#B45309', dark: false },
    { key: 'rose', label: 'Rosa', bg: '#FDF2F5', card: '#FFFFFF', accent: '#BE185D', dark: false },
    { key: 'dark', label: 'Oscuro', bg: '#0C0D10', card: '#1A1D24', accent: '#14B8A6', dark: true },
  ];

  // Fondo opcional y tema de la tienda — el portal usa el tema para superficies y modales.
  const isDarkTheme = isDarkStoreTheme(draft.storeTheme);
  const backgroundBlock = (
    <PaperSheet padded>
      <Text style={styles.cardTitle}>Apariencia y fondo</Text>

      <View style={[local.appearanceGrid, { marginTop: spacing.xs }]}>
        {/* Columna 1: Selector de color/tema de fondo */}
        <View style={local.appearanceThemeCol}>
          <View style={local.labelRowInline}>
            <Text style={local.fieldLabel}>Color de fondo y tema</Text>
            <Text style={local.inlineMetaItalic}>
              {isDarkTheme ? 'Modo oscuro' : 'Modo claro'}
            </Text>
          </View>
          <View style={local.colorSwatchesGrid}>
            {THEME_PALETTES.map((item) => {
              const active = (draft.storeTheme ?? 'light') === item.key;
              return (
                <TouchableOpacity
                  key={item.key}
                  disabled={!canEditProfile || saving}
                  onPress={() => onChange({ storeTheme: item.key })}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  accessibilityLabel={`Color ${item.label}`}
                  style={[
                    local.colorSwatchItem,
                    active && local.colorSwatchItemActive,
                    (!canEditProfile || saving) && styles.disabled,
                  ]}
                >
                  <View
                    style={[
                      local.colorSwatchPreview,
                      {
                        backgroundColor: item.bg,
                        borderColor: item.dark
                          ? 'rgba(255,255,255,0.16)'
                          : 'rgba(28,25,23,0.14)',
                      },
                    ]}
                  >
                    <View
                      style={[
                        local.colorSwatchInnerCard,
                        {
                          backgroundColor: item.card,
                          borderColor: item.dark
                            ? 'rgba(255,255,255,0.12)'
                            : 'rgba(28,25,23,0.08)',
                        },
                      ]}
                    >
                      <View
                        style={[
                          local.colorSwatchAccentBar,
                          { backgroundColor: item.accent },
                        ]}
                      />
                    </View>
                  </View>
                  <Text
                    style={[
                      local.colorSwatchLabel,
                      active && local.colorSwatchLabelActive,
                    ]}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          {draft.backgroundStoragePath && !isDarkTheme ? (
            <Text style={[local.inlineMetaItalic, { marginTop: 6 }]}>
              Con imagen de fondo se luce mejor el tema Oscuro.
            </Text>
          ) : null}
        </View>

        {/* Columna 2: Imagen de fondo */}
        <View style={local.appearanceBgCol}>
          <Text style={local.fieldLabel}>Imagen de fondo (opcional)</Text>
          {draft.backgroundStoragePath ? (
            <View style={{ marginTop: 8 }}>
              <View style={local.bgPreviewFrame}>
                <Image
                  source={{ uri: draft.backgroundStoragePath }}
                  style={local.bgPreviewImage}
                  resizeMode="cover"
                />
              </View>
              {canEditProfile ? (
                <View style={local.coverOverlayActions}>
                  {actionBtn(backgroundUploading ? 'Subiendo…' : 'Cambiar fondo', onUploadBackground, {
                    disabled: backgroundUploading || saving,
                  })}
                  {actionBtn('Quitar fondo', onRemoveBackground, {
                    ghost: true,
                    disabled: saving || backgroundUploading,
                  })}
                </View>
              ) : null}
            </View>
          ) : canEditProfile ? (
            <View style={[local.coverPlaceholder, local.emptyMediaBox, { marginTop: 8, height: 112 }]}>
              {backgroundUploading ? (
                <ActivityIndicator color={ticketColors.ink2} />
              ) : (
                <>
                  <Text style={local.emptyMediaTitle}>Sin imagen de fondo</Text>
                  <View style={{ marginTop: 8 }}>
                    {actionBtn('Subir fondo', onUploadBackground, {
                      disabled: backgroundUploading || saving,
                    })}
                  </View>
                </>
              )}
            </View>
          ) : (
            <View style={[local.coverPlaceholder, local.emptyMediaBox, { marginTop: 8, height: 112 }]}>
              <Text style={styles.caption}>Sin imagen de fondo</Text>
            </View>
          )}
        </View>
      </View>
    </PaperSheet>
  );

  const logoActionsInline = canEditProfile ? (
    <View style={local.logoActionsInlineRow}>
      {actionBtn(logoUploading ? 'Subiendo…' : 'Cambiar logo', onUploadLogo, {
        disabled: logoUploading || saving,
      })}
      {draft.logoUrl
        ? actionBtn('Quitar', onRemoveLogo, {
            ghost: true,
            disabled: saving || logoUploading,
          })
        : null}
    </View>
  ) : null;

  const vitrinaBlock = (
    <PaperSheet padded>
      <Text style={styles.cardTitle}>Identidad, portada y logo</Text>

      <View style={[local.mediaGrid, { marginTop: spacing.xs }]}>
        {/* Columna Portada */}
        <View style={local.coverCol}>
          <View style={local.labelRowInline}>
            <Text style={local.fieldLabel}>Portada (opcional · 16:9)</Text>
            {draft.coverStoragePath && canEditProfile ? (
              <Text style={local.inlineMetaItalic}>Arrastrá para encuadrar</Text>
            ) : null}
          </View>
          <View style={{ marginTop: 6 }}>
            {draft.coverStoragePath ? (
              <View>
                <ImageFramingEditor
                  uri={draft.coverStoragePath}
                  aspect={16 / 9}
                  focalX={draft.coverFocalX}
                  focalY={draft.coverFocalY}
                  zoom={draft.coverZoom}
                  editable={canEditProfile}
                  testID="storefront-cover-editor"
                  onFocalChange={(x, y) => onChange({ coverFocalX: x, coverFocalY: y })}
                  onZoomChange={(coverZoom) => onChange({ coverZoom })}
                  sideActions={
                    canEditProfile ? (
                      <View style={local.logoActionsInlineRow}>
                        {actionBtn(coverUploading ? 'Subiendo…' : 'Cambiar portada', onUploadCover, {
                          disabled: coverUploading || saving,
                        })}
                        {actionBtn('Quitar', onRemoveCover, {
                          ghost: true,
                          disabled: saving || coverUploading,
                        })}
                      </View>
                    ) : null
                  }
                />
              </View>
            ) : canEditProfile ? (
              <View style={[local.coverPlaceholder, local.emptyMediaBox, { height: 116 }]}>
                {coverUploading ? (
                  <ActivityIndicator color={ticketColors.ink2} />
                ) : (
                  <>
                    <Text style={local.emptyMediaTitle}>Sin portada</Text>
                    <View style={{ marginTop: 8 }}>
                      {actionBtn('Subir portada', onUploadCover, {
                        disabled: coverUploading || saving,
                      })}
                    </View>
                  </>
                )}
              </View>
            ) : (
              <View style={[local.coverPlaceholder, local.emptyMediaBox, { height: 116 }]}>
                <Text style={styles.caption}>Sin portada</Text>
              </View>
            )}
          </View>
        </View>

        {/* Columna Logo compacta: preview a la izquierda + controles y acciones al lado */}
        <View style={local.logoCol}>
          <View style={local.labelRowInline}>
            <Text style={local.fieldLabel}>Logo</Text>
            {draft.logoUrl && canEditProfile ? (
              <Text style={local.inlineMetaItalic}>Arrastrá para encuadrar</Text>
            ) : null}
          </View>
          <View style={{ marginTop: 6 }}>
            {draft.logoUrl ? (
              <ImageFramingEditor
                uri={draft.logoUrl}
                aspect={1}
                focalX={draft.logoFocalX}
                focalY={draft.logoFocalY}
                zoom={draft.logoZoom}
                editable={canEditProfile}
                testID="storefront-logo-editor"
                frameWidth={116}
                sideActions={logoActionsInline}
                onFocalChange={(x, y) => onChange({ logoFocalX: x, logoFocalY: y })}
                onZoomChange={(logoZoom) => onChange({ logoZoom })}
              />
            ) : (
              <View style={local.logoHorizontalRow}>
                <View style={local.logoOverlap}>
                  <Text style={local.logoOverlapText}>
                    {(branchName || 'T').slice(0, 2).toUpperCase()}
                  </Text>
                </View>
                {logoActionsInline}
              </View>
            )}
          </View>
        </View>
      </View>

      <View style={[local.identityPad, local.pedidosDivider]}>
        <View style={local.twoCol}>
          <View style={local.twoColItem}>
            <View style={local.labelRowInline}>
              <Text style={local.fieldLabel}>Nombre de la tienda</Text>
              <Text style={local.inlineMetaItalic}>Opcional</Text>
            </View>
            <TextInput
              style={[styles.input, { marginTop: 4 }]}
              value={draft.displayName}
              onChangeText={(displayName) => onChange({ displayName })}
              placeholder={branchName || 'Nombre de tu negocio'}
              placeholderTextColor={ticketColors.ink3}
              maxLength={120}
              editable={canEditProfile && !saving}
            />
          </View>

          <View style={local.twoColItem}>
            <View style={local.labelRowInline}>
              <Text style={local.fieldLabel}>Link de la tienda</Text>
              {slugCheck === 'checking' ? (
                <Text style={local.slugStatusInline}>Verificando…</Text>
              ) : slugCheck === 'ok' ? (
                <Text style={[local.slugStatusInline, { color: ticketColors.ok }]}>
                  ✓ disponible
                </Text>
              ) : slugCheck === 'taken' ? (
                <Text style={[local.slugStatusInline, { color: ticketColors.bad }]}>
                  Ya está en uso
                </Text>
              ) : slugCheck === 'invalid' ? (
                <Text style={[local.slugStatusInline, { color: ticketColors.bad }]}>
                  Solo minúsculas, números y guiones
                </Text>
              ) : null}
            </View>
            <View style={[local.slugPrefix, { marginTop: 4 }]}>
              <Text style={local.prefixLabel}>vuelto.ar/</Text>
              <TextInput
                style={[styles.input, local.prefixInput]}
                value={draft.slug}
                onChangeText={(slug) => onChange({ slug })}
                placeholder="ej. kiosco-palermo"
                placeholderTextColor={ticketColors.ink3}
                autoCapitalize="none"
                autoCorrect={false}
                editable={canEditProfile && !saving}
              />
            </View>
          </View>
        </View>

        <View>
          <View style={local.labelRowInline}>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap', gap: 6, flex: 1 }}>
              <Text style={local.fieldLabel}>Descripción pública</Text>
              <Text style={local.inlineMetaItalic}>
                Se ve en la info de tu tienda (ideal 1–2 líneas)
              </Text>
            </View>
            <Text style={local.inlineMetaItalic}>{draft.description.length} car.</Text>
          </View>
          <TextInput
            style={[styles.input, local.multiline, { marginTop: 4 }]}
            value={draft.description}
            onChangeText={(description) => onChange({ description })}
            placeholder="Qué vendés, especialidad, horarios destacados…"
            placeholderTextColor={ticketColors.ink3}
            multiline
            textAlignVertical="top"
            editable={canEditProfile && !saving}
          />
        </View>
      </View>
    </PaperSheet>
  );

  // —— Contacto ——

  const contactBlock = (
    <PaperSheet padded>
      <Text style={styles.cardTitle}>Contacto</Text>
      <View style={[local.twoCol, { marginTop: spacing.xs }]}>
        <View style={local.twoColItem}>
          <PhonePrefixField
            label="Teléfono"
            value={draft.phone}
            onChangeText={(phone) => {
              if (!phone.trim() && draft.orderContactPhone) {
                showInfo('Llamar en el pedido', 'Se desactivó porque quitaste el teléfono.');
              }
              onChange({ phone });
            }}
            editable={canEditProfile && !saving}
            inputRef={phoneRef}
            placeholder="11 5555-5555"
          />
        </View>
        <View style={local.twoColItem}>
          <PhonePrefixField
            label="WhatsApp"
            value={draft.whatsapp}
            onChangeText={(whatsapp) => {
              if (!whatsapp.trim() && draft.orderContactWhatsapp) {
                showInfo('WhatsApp en el pedido', 'Se desactivó porque quitaste el número.');
              }
              onChange({ whatsapp });
            }}
            editable={canEditProfile && !saving}
            inputRef={whatsappRef}
            placeholder="11 5555-5555"
          />
        </View>
      </View>
      <Text style={[local.fieldLabel, { marginTop: spacing.md }]}>En el pedido, el cliente puede</Text>
      <View style={local.switchStack}>
        <View style={local.rowBetween}>
          <View style={{ flex: 1, paddingRight: spacing.md }}>
            <Text style={styles.captionStrong}>Escribirte un mensaje</Text>
            <Text style={styles.caption}>Chat desde el seguimiento del pedido.</Text>
          </View>
          <Switch
            value={draft.orderContactMessage}
            onValueChange={(orderContactMessage) => onChange({ orderContactMessage })}
            disabled={!canEditProfile || saving}
            trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
            thumbColor={ticketColors.ink}
          />
        </View>
        <View style={[local.rowBetween, local.pedidosDivider]}>
          <View style={{ flex: 1, paddingRight: spacing.md }}>
            <Text style={styles.captionStrong}>Contactarte por WhatsApp</Text>
            {!hasWhatsapp ? (
              <Text style={styles.caption}>Cargá tu WhatsApp arriba para activarlo.</Text>
            ) : null}
          </View>
          <Switch
            value={draft.orderContactWhatsapp}
            onValueChange={tryEnableWhatsappContact}
            disabled={!canEditProfile || saving}
            trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
            thumbColor={ticketColors.ink}
          />
        </View>
        <View style={[local.rowBetween, local.pedidosDivider]}>
          <View style={{ flex: 1, paddingRight: spacing.md }}>
            <Text style={styles.captionStrong}>Llamarte por teléfono</Text>
            {!hasPhone ? (
              <Text style={styles.caption}>Cargá tu teléfono arriba para activarlo.</Text>
            ) : null}
          </View>
          <Switch
            value={draft.orderContactPhone}
            onValueChange={tryEnablePhoneContact}
            disabled={!canEditProfile || saving}
            trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
            thumbColor={ticketColors.ink}
          />
        </View>
      </View>
    </PaperSheet>
  );

  // —— Pedidos ——

  const pedidosBlock = draft.isPublic ? (
    <PaperSheet padded>
      <Text style={styles.cardTitle}>Pedidos</Text>
      <Text style={styles.caption}>
        Límites operativos y avisos automáticos para pedidos online.
      </Text>
      <View style={[local.twoCol, { marginTop: spacing.sm }]}>
        <View style={local.twoColItem}>
          <Text style={local.fieldLabel}>Pedidos a la vez</Text>
          <Text style={local.fieldHint}>Tope de pedidos abiertos. Vacío = sin límite.</Text>
          <TextInput
            style={[styles.input, { marginTop: 4 }]}
            value={
              draft.maxConcurrentOrders != null && draft.maxConcurrentOrders > 0
                ? String(draft.maxConcurrentOrders)
                : ''
            }
            onChangeText={(raw) => {
              const digits = raw.replace(/[^\d]/g, '');
              if (!digits) {
                onChange({ maxConcurrentOrders: null });
                return;
              }
              const n = Number(digits);
              onChange({ maxConcurrentOrders: Number.isFinite(n) && n > 0 ? n : null });
            }}
            placeholder="Sin límite"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="number-pad"
            editable={canEditProfile && !saving}
          />
        </View>
        <View style={local.twoColItem}>
          <Text style={local.fieldLabel}>Monto mínimo de pedido</Text>
          <Text style={local.fieldHint}>Retiro y delivery. Vacío = sin mínimo.</Text>
          <TextInput
            testID="store.profile.min-order-amount"
            style={[styles.input, { marginTop: 4 }]}
            value={
              draft.minOrderAmount != null && draft.minOrderAmount > 0
                ? String(draft.minOrderAmount)
                : ''
            }
            onChangeText={(raw) => {
              const digits = raw.replace(/[^\d.,]/g, '').replace(',', '.');
              if (!digits) {
                onChange({ minOrderAmount: null });
                return;
              }
              const n = Number(digits);
              onChange({ minOrderAmount: Number.isFinite(n) && n > 0 ? n : null });
            }}
            placeholder="Sin mínimo"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="decimal-pad"
            editable={canEditProfile && !saving}
          />
        </View>
        <View style={local.twoColItem}>
          <Text style={local.fieldLabel}>Minutos de preparación</Text>
          <Text style={local.fieldHint}>Tiempo estimado en la tienda. Vacío = no mostramos.</Text>
          <TextInput
            style={[styles.input, { marginTop: 4 }]}
            value={
              draft.defaultPrepMinutes != null && draft.defaultPrepMinutes > 0
                ? String(draft.defaultPrepMinutes)
                : ''
            }
            onChangeText={(raw) => {
              const digits = raw.replace(/[^\d]/g, '');
              if (!digits) {
                onChange({ defaultPrepMinutes: null });
                return;
              }
              const n = Number(digits);
              if (!Number.isFinite(n) || n <= 0) {
                onChange({ defaultPrepMinutes: null });
                return;
              }
              onChange({ defaultPrepMinutes: Math.min(120, Math.max(5, n)) });
            }}
            placeholder="ej. 15"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="number-pad"
            editable={canEditProfile && !saving}
          />
          <View style={local.prepChips}>
            {[10, 15, 20, 30, 45].map((mins) => {
              const active = draft.defaultPrepMinutes === mins;
              return (
                <TouchableOpacity
                  key={mins}
                  style={[local.prepChip, active && local.prepChipActive]}
                  onPress={() => onChange({ defaultPrepMinutes: mins })}
                  disabled={!canEditProfile || saving}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                >
                  <Text style={[local.prepChipText, active && local.prepChipTextActive]}>{mins}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>
      <View style={[local.rowBetween, local.pedidosDivider]}>
        <View style={{ flex: 1, paddingRight: spacing.md }}>
          <Text style={styles.captionStrong}>Aceptar automáticamente retiros ya pagos</Text>
          <Text style={styles.caption}>
            Los pedidos con envío o pago en el local los confirmás vos.
          </Text>
        </View>
        <Switch
          value={draft.autoAcceptPickupPaid === true}
          onValueChange={(autoAcceptPickupPaid) => onChange({ autoAcceptPickupPaid })}
          disabled={!canEditProfile || saving}
          trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
          thumbColor={ticketColors.ink}
        />
      </View>
      <NotifyCustomerReadyPickupSwitchRow
        style={local.pedidosDivider}
        value={draft.notifyCustomerReadyPickup === true}
        onValueChange={(notifyCustomerReadyPickup) => onChange({ notifyCustomerReadyPickup })}
        disabled={!canEditProfile || saving}
        hasKitchenTickets={kitchenTicketsEnabled}
        testID="storefront.hub.notify-ready-pickup"
      />
    </PaperSheet>
  ) : null;

  // —— Horarios ——

  const hoursBlock = (
    <StoreHoursEditor
      draft={draft}
      canEdit={canEditHours}
      saving={saving}
      onBusinessHourChange={onBusinessHourChange}
      onAddShift={onAddShift}
      onRemoveShift={onRemoveShift}
      onShiftChange={onShiftChange}
    />
  );

  // —— QR ——

  const qrSection = draft.isPublic ? (
    <QrGeneratorSection
      slug={draft.slug}
      branchName={branchName}
      portalBaseUrl={portalBaseUrl}
    />
  ) : null;

  // —— Carta presencial ——

  const cartaBlock = draft.isPublic ? (
    <PaperSheet padded>
      <View style={local.rowBetween}>
        <View style={{ flex: 1, paddingRight: spacing.md }}>
          <Text style={styles.captionStrong}>Carta en el local</Text>
          <Text style={styles.caption}>
            «Ver la carta» en la tienda y QR para consultar sin pedir online.
          </Text>
        </View>
        <Switch
          value={draft.menuCartaEnabled}
          onValueChange={(menuCartaEnabled) => onChange({ menuCartaEnabled })}
          disabled={!canEditProfile || saving}
          trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
          thumbColor={ticketColors.ink}
        />
      </View>
    </PaperSheet>
  ) : null;

  // #904: QR menú presencial — read-only catalog, sin pedidos online.
  const menuQrSection = draft.isPublic && draft.menuCartaEnabled ? (
    <QrGeneratorSection
      slug={draft.slug}
      branchName={branchName}
      portalBaseUrl={portalBaseUrl}
      variant="menu"
    />
  ) : null;

  return (
    <View style={local.stack}>
      {door}
      {onlineToggle}
      {dutySwitch}
      {copyLink}
      {vitrinaBlock}
      {backgroundBlock}
      {contactBlock}
      {pedidosBlock}
      {cartaBlock}
      {hoursBlock}
      {qrSection}
      {menuQrSection}
    </View>
  );
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;
const clampZoom = (z: number) =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));
const formatZoom = (z: number) =>
  `×${z.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}`;

/** Editable framed crop (drag + zoom). The image moves inside the frame; focal + zoom are persisted. */
function ImageFramingEditor({
  uri,
  aspect,
  focalX,
  focalY,
  zoom,
  editable = true,
  testID,
  hint,
  frameWidth,
  sideActions,
  onFocalChange,
  onZoomChange,
}: {
  uri: string;
  aspect: number;
  focalX: number;
  focalY: number;
  zoom: number;
  editable?: boolean;
  testID?: string;
  hint?: string;
  /** Si se pasa (ej. 116 para el logo), el frame tiene ancho fijo y los controles van al lado. */
  frameWidth?: number;
  sideActions?: React.ReactNode;
  onFocalChange: (x: number, y: number) => void;
  onZoomChange: (z: number) => void;
}) {
  const frameRef = useRef<View>(null);
  const imageSizeRef = useRef({ width: 16, height: 9 });
  const frameSizeRef = useRef({ width: 0, height: 0 });
  const liveRef = useRef({ focalX, focalY, zoom, onFocalChange, onZoomChange });
  const dragRef = useRef<{ x: number; y: number; focalX: number; focalY: number } | null>(null);
  const [frameReady, setFrameReady] = useState(false);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = useState({ width: 16, height: 9 });

  useEffect(() => {
    liveRef.current = { focalX, focalY, zoom, onFocalChange, onZoomChange };
  });

  useEffect(() => {
    Image.getSize(
      uri,
      (width, height) => {
        imageSizeRef.current = { width, height };
        setImageSize({ width, height });
      },
      () => undefined,
    );
  }, [uri]);

  const computeCrop = (
    frame: { width: number; height: number },
    source: { width: number; height: number },
    z: number,
  ) => {
    if (!frame.width || !frame.height || !source.width || !source.height) return null;
    const scale = Math.max(frame.width / source.width, frame.height / source.height) * Math.max(1, z);
    const width = source.width * scale;
    const height = source.height * scale;
    return {
      width,
      height,
      maxX: Math.max(0, (width - frame.width) / 2),
      maxY: Math.max(0, (height - frame.height) / 2),
    };
  };

  const focalToTranslation = (
    crop: { maxX: number; maxY: number },
    x: number,
    y: number,
  ) => ({
    x: ((50 - x) / 50) * crop.maxX,
    y: ((50 - y) / 50) * crop.maxY,
  });

  // Live crop for event handlers (reads refs — never during render).
  const getCrop = () =>
    computeCrop(frameSizeRef.current, imageSizeRef.current, liveRef.current.zoom);

  const updateFromDrag = (dx: number, dy: number) => {
    const start = dragRef.current;
    if (!start) return;
    const crop = getCrop();
    if (!crop) return;
    const startTranslation = focalToTranslation(crop, start.focalX, start.focalY);
    const x = Math.max(-crop.maxX, Math.min(crop.maxX, startTranslation.x + dx));
    const y = Math.max(-crop.maxY, Math.min(crop.maxY, startTranslation.y + dy));
    const next = {
      x: Math.max(0, Math.min(100, 50 - (x / Math.max(1, crop.maxX)) * 50)),
      y: Math.max(0, Math.min(100, 50 - (y / Math.max(1, crop.maxY)) * 50)),
    };
    liveRef.current.onFocalChange(next.x, next.y);
  };

  const beginDrag = (x: number, y: number) => {
    dragRef.current = { x, y, focalX: liveRef.current.focalX, focalY: liveRef.current.focalY };
  };

  const moveDrag = (x: number, y: number) => {
    const start = dragRef.current;
    if (start) updateFromDrag(x - start.x, y - start.y);
  };

  // PanResponder.create solo registra callbacks (patrón RN); no lee refs al crear.
  // eslint-disable-next-line react-hooks/refs
  const [panResponder] = useState(() =>
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (event) => {
        beginDrag(event.nativeEvent.pageX, event.nativeEvent.pageY);
      },
      onPanResponderMove: (event) => {
        moveDrag(event.nativeEvent.pageX, event.nativeEvent.pageY);
      },
      onPanResponderRelease: () => {
        dragRef.current = null;
      },
      onPanResponderTerminate: () => {
        dragRef.current = null;
      },
    }),
  );

  useEffect(() => {
    if (Platform.OS !== 'web' || !editable) return;
    const element = frameRef.current as unknown as HTMLElement | null;
    if (!element) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const current = liveRef.current;
      current.onZoomChange(clampZoom(current.zoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)));
    };
    const onPointerDown = (event: PointerEvent) => {
      event.preventDefault();
      element.setPointerCapture?.(event.pointerId);
      beginDrag(event.clientX, event.clientY);
    };
    const onPointerMove = (event: PointerEvent) => {
      if (dragRef.current) {
        event.preventDefault();
        moveDrag(event.clientX, event.clientY);
      }
    };
    const onPointerUp = () => { dragRef.current = null; };
    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerup', onPointerUp);
    element.addEventListener('pointercancel', onPointerUp);
    element.addEventListener('wheel', onWheel, { passive: false });
    element.style.cursor = 'grab';
    element.style.touchAction = 'none';
    return () => {
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerUp);
      element.removeEventListener('pointercancel', onPointerUp);
      element.removeEventListener('wheel', onWheel);
    };
    // Handlers leen liveRef — no dependen de props del render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, frameReady]);

  const crop = computeCrop(frameSize, imageSize, zoom);
  const translation = crop ? focalToTranslation(crop, focalX, focalY) : { x: 0, y: 0 };

  const frameNode = (
    <View
      ref={(node) => {
        frameRef.current = node;
        setFrameReady(Boolean(node));
      }}
      testID={testID}
      style={[
        local.coverPreview16x9,
        { aspectRatio: aspect },
        frameWidth ? { width: frameWidth, height: frameWidth / aspect, flexShrink: 0 } : null,
      ]}
      {...(Platform.OS === 'web' || !editable ? {} : panResponder.panHandlers)}
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout;
        frameSizeRef.current = { width, height };
        setFrameSize({ width, height });
      }}
    >
      <Image
        source={{ uri }}
        onLoad={(event) => {
          const source = event.nativeEvent.source;
          if (source?.width && source?.height) {
            imageSizeRef.current = { width: source.width, height: source.height };
            setImageSize({ width: source.width, height: source.height });
          }
        }}
        style={[
          local.coverEditorImage,
          {
            width: crop?.width ?? '100%',
            height: crop?.height ?? '100%',
            left: '50%',
            top: '50%',
            marginLeft: -(crop?.width ?? 0) / 2 + translation.x,
            marginTop: -(crop?.height ?? 0) / 2 + translation.y,
          },
        ]}
        resizeMode="stretch"
      />
    </View>
  );

  const zoomControlsNode = editable ? (
    <View style={[local.zoomRow, frameWidth ? { marginTop: 0 } : null]}>
      <TouchableOpacity
        style={[local.zoomBtn, zoom <= ZOOM_MIN && styles.disabled]}
        onPress={() => onZoomChange(clampZoom(zoom - ZOOM_STEP))}
        disabled={zoom <= ZOOM_MIN}
        accessibilityRole="button"
        accessibilityLabel="Alejar"
      >
        <Text style={local.zoomBtnText}>−</Text>
      </TouchableOpacity>
      <Text style={local.zoomValue}>{formatZoom(zoom)}</Text>
      <TouchableOpacity
        style={[local.zoomBtn, zoom >= ZOOM_MAX && styles.disabled]}
        onPress={() => onZoomChange(clampZoom(zoom + ZOOM_STEP))}
        disabled={zoom >= ZOOM_MAX}
        accessibilityRole="button"
        accessibilityLabel="Acercar"
      >
        <Text style={local.zoomBtnText}>+</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={local.zoomReset}
        onPress={() => {
          onFocalChange(50, 50);
          onZoomChange(1);
        }}
        accessibilityRole="button"
        accessibilityLabel="Recentrar"
      >
        <Text style={local.zoomResetText}>Recentrar</Text>
      </TouchableOpacity>
    </View>
  ) : null;

  if (frameWidth) {
    return (
      <View style={local.logoHorizontalRow}>
        {frameNode}
        <View style={local.logoSideControls}>
          {zoomControlsNode}
          {sideActions}
          {hint ? <Text style={[local.focalHint, { textAlign: 'left', marginTop: 0 }]}>{hint}</Text> : null}
        </View>
      </View>
    );
  }

  return (
    <View>
      {frameNode}
      <View style={local.coverBottomControlsRow}>
        {zoomControlsNode}
        {sideActions}
      </View>
      {hint ? <Text style={local.focalHint}>{hint}</Text> : null}
    </View>
  );
}

const local = StyleSheet.create({
  stack: {
    gap: 12,
    alignSelf: 'stretch',
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  fieldHint: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 17,
    marginTop: 2,
  },
  labelRowInline: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 6,
  },
  slugStatusInline: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  inlineMetaItalic: {
    fontSize: 11,
    fontStyle: 'italic',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  mediaGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    alignItems: 'flex-start',
  },
  coverCol: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 300,
    minWidth: 260,
    maxWidth: 460,
  },
  logoCol: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 300,
    minWidth: 260,
    maxWidth: 420,
  },
  logoHorizontalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  logoSideControls: {
    flex: 1,
    gap: 10,
    justifyContent: 'center',
  },
  logoActionsInlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 8,
  },
  coverBottomControlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  appearanceGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    alignItems: 'flex-start',
  },
  appearanceThemeCol: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 340,
    minWidth: 260,
    maxWidth: 480,
  },
  appearanceBgCol: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 260,
    minWidth: 240,
    maxWidth: 380,
  },
  colorSwatchesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  colorSwatchItem: {
    width: 78,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: 6,
    alignItems: 'center',
    gap: 5,
  },
  colorSwatchItemActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  colorSwatchPreview: {
    width: '100%',
    height: 40,
    borderRadius: 6,
    borderWidth: 1,
    padding: 5,
    justifyContent: 'flex-end',
  },
  colorSwatchInnerCard: {
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
    paddingHorizontal: 4,
    justifyContent: 'center',
  },
  colorSwatchAccentBar: {
    width: 16,
    height: 4,
    borderRadius: 2,
  },
  colorSwatchLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  colorSwatchLabelActive: {
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
  bgPreviewFrame: {
    width: '100%',
    height: 132,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    overflow: 'hidden',
  },
  bgPreviewImage: {
    width: '100%',
    height: '100%',
  },
  emptyMediaBox: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  emptyMediaTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  emptyMediaSub: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
    marginTop: 2,
  },
  rowBetween: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  twoCol: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  twoColItem: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 240,
    minWidth: 220,
    maxWidth: '100%',
  },
  switchStack: {
    marginTop: spacing.xs,
    gap: spacing.md,
  },
  coverBlock: {
    position: 'relative',
    maxWidth: 560,
    width: '100%',
    marginBottom: 8,
  },
  coverPreview16x9: {
    position: 'relative',
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    overflow: 'hidden',
  },
  coverEditorImage: {
    position: 'absolute',
  },
  zoomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  zoomBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  zoomBtnText: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  zoomValue: {
    minWidth: 44,
    textAlign: 'center',
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  zoomReset: {
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  zoomResetText: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    textDecorationLine: 'underline',
  },
  logoEditorWrap: {
    width: 180,
    maxWidth: '100%',
    marginTop: 4,
  },
  focalHint: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    marginTop: 6,
    textAlign: 'center',
  },
  coverPlaceholder: {
    position: 'relative',
    height: 140,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    overflow: 'hidden',
  },
  coverOverlayActions: {
    position: 'relative',
    alignSelf: 'flex-start',
    marginTop: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  coverEmptyActions: {
    position: 'absolute',
    top: 8,
    right: 8,
    flexDirection: 'row',
    gap: 8,
  },
  actionBtn: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: 8,
    paddingHorizontal: 12,
    minHeight: 36,
    justifyContent: 'center',
  },
  actionBtnGhost: {
    backgroundColor: 'transparent',
    borderColor: ticketColors.ruleSoft,
  },
  actionBtnPrimary: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  actionBtnOnCover: {
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderColor: 'transparent',
  },
  actionBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  actionBtnTextGhost: {
    color: ticketColors.ink2,
  },
  actionBtnTextPrimary: {
    color: ticketColors.goldInk,
  },
  logoOverlap: {
    position: 'relative',
    alignSelf: 'flex-start',
    marginTop: 10,
    width: 64,
    height: 64,
    borderRadius: 14,
    backgroundColor: ticketColors.paper,
    borderWidth: 2,
    borderColor: ticketColors.paper,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoOverlapText: {
    fontSize: 22,
    fontWeight: '800',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  identityPad: {
    gap: spacing.md,
    paddingTop: spacing.sm,
  },
  btnRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  slugPrefix: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  prefixLabel: {
    paddingHorizontal: 10,
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  prefixInput: {
    borderWidth: 0,
    borderRadius: 0,
    flex: 1,
  },
  multiline: {
    minHeight: 80,
  },
  prepChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: spacing.xs,
  },
  prepChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  prepChipActive: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.gold,
  },
  prepChipText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  prepChipTextActive: {
    color: ticketColors.brandInk,
  },
  pedidosDivider: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
});
